/**
 * Property — the collateral, and the address the market read is built from.
 *
 * The address row leads the card for the same reason the RTL sizer's property
 * card leads with it: `POST /pricing/scenario/dscr_quick` GATES on
 * `address.zip` (422), because market data and the CFPB rural determination
 * embed into the price. A full address resolves rural at address level; a bare
 * ZIP falls back to the county crosswalk — so the picker takes 8 of 12 and the
 * ZIP box is the floor beside it, not an equal option.
 */
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import {
  SZ,
  Section,
  Grid,
  Field2,
  MoneyInput,
  NumInput,
  SelectInput,
  SegmentedGroup,
  TextInput,
} from "../../sizersShared/sizerUi";
import SizerAddressInput from "../../sizersShared/SizerAddressInput";
import { PROPERTY_TYPES, YES_NO } from "../dscrConstants";
import { unitsForPropertyType } from "../../../constants/selectOptions/unitsByPropertyType";
import GateError from "./GateError";
import { isPurchaseLike } from "../../../utils/loans/delayedPurchase";

export default function PropertySection({ form, set, gate }) {
  const address = form.address || {};
  // Units the selected type encodes (null = entered by hand).
  const typeUnits = unitsForPropertyType(form.propertyType);
  // A delayed purchase counts (REQ-106 parity rule).
  const isPurchase = isPurchaseLike(form.loanPurpose, form.delayedPurchase);

  // Typing a ZIP by hand is the fallback path, so it edits ONLY the zip and
  // leaves whatever the picker resolved alone — clearing the street address
  // because someone corrected a ZIP would throw away the address-level rural
  // determination that address bought.
  const setZip = (zip) => set("address", { ...address, zip });

  return (
    <Section
      first
      title="Property"
      subtitle="The collateral, and the market the price is made against."
    >
      <Grid cols={12}>
        <Field2
          label="Property address"
          span={8}
          tip="A full address resolves the CFPB rural check at address level (HouseCanary census / census.gov FIPS). ZIP alone falls back to the county crosswalk."
        >
          <SizerAddressInput
            value={address}
            onChange={(next) => set("address", next)}
          />
        </Field2>
        <Field2 label="ZIP" span={4} required>
          <TextInput
            mono
            inputMode="numeric"
            maxLength={5}
            value={address.zip || ""}
            onChange={(e) => setZip(e.target.value)}
            placeholder="33131"
          />
          <GateError fields={gate} name="address.zip" />
        </Field2>
      </Grid>

      {/* What the picker actually resolved. Not a hint under a control — it is
          the confirmation that the address the rural check will run against is
          the one the user meant, so it reads on its own line. */}
      {(address.city || address.county || address.state) && (
        <Flx g={0.75} sx={{ mt: "8px", flexWrap: "wrap", alignItems: "baseline" }}>
          <Txt sx={{ fontSize: 11.5, color: SZ.ink400 }}>Resolved</Txt>
          <Txt sx={{ fontSize: 11.5, color: SZ.ink700, fontWeight: 500 }}>
            {[address.city, address.county, address.state]
              .filter(Boolean)
              .join(" · ")}
          </Txt>
        </Flx>
      )}

      <Grid cols={12} sx={{ mt: "26px" }}>
        {/* Sent as `propertyType`: the backend derives the unit count (and the
            condo flags) from it, which is what the 2–4 / 5–9 unit pricing
            adjustments key off. Not required — the model doesn't require it. */}
        <Field2 label="Property type" span={6}>
          <SelectInput
            value={form.propertyType}
            onChange={(v) => {
              set("propertyType", v);
              // Keep the stored count in step with the type that decides it.
              const n = unitsForPropertyType(v);
              if (n != null) set("unitCount", n);
            }}
            options={PROPERTY_TYPES}
            placeholder="Select property type"
          />
        </Field2>
        {/* Drives the 2-4 Unit / 5-9 Unit LLPA rows. A LOOKUP on the property
            type whenever the type encodes a count (the backend overrides any
            other number); entered by hand only for Multifamily / Mixed-Use /
            Commercial. */}
        <Field2
          label="Units"
          span={3}
          tip={
            typeUnits != null
              ? "Set by the property type."
              : "Enter the total units for this property type."
          }
        >
          {typeUnits != null ? (
            <NumInput value={typeUnits} readOnly disabled />
          ) : (
            <NumInput
              value={form.unitCount}
              onChange={(v) => set("unitCount", v)}
            />
          )}
        </Field2>
        {/* A real boolean on the model — a segmented switch, not a select, so
            the two states read at a glance and neither posts a string. */}
        <Field2 label="Cross-collateralized" span={3}>
          <SegmentedGroup
            value={form.crossCollateralized}
            onChange={(v) => set("crossCollateralized", v)}
            options={YES_NO}
            fallback={false}
          />
        </Field2>
      </Grid>

      <Grid cols={2} sx={{ mt: "26px" }}>
        <Field2 label="As-is / appraised value" required>
          <MoneyInput
            value={form.asIsValue}
            onChange={(v) => set("asIsValue", v)}
          />
        </Field2>
        {/* REQUIRED on a purchase (the input model rejects it otherwise — no
            price means no price-based leverage); on a refinance it is the
            OPTIONAL original purchase price, basis context only. */}
        <Field2
          label="Purchase price"
          required={isPurchase}
          tip={
            isPurchase
              ? "Leverage prices off the LOWER of the appraised value and the purchase price."
              : "Optional on a refinance — original basis context only."
          }
        >
          <MoneyInput
            value={form.purchasePrice}
            onChange={(v) => set("purchasePrice", v)}
          />
        </Field2>
      </Grid>
    </Section>
  );
}
